import React, { useEffect } from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { WhiteboardEnablePermission } from '@/types/whiteboard';
import { profile } from '@/utils/common';
import { message } from '@/components';
import { RootStore } from '@/models';
import * as API from '@/api';
import AppsBtn from './apps';
import { elkLog, elkReport } from '@/utils/elkReport';
import { event } from '@/.umi/plugin-locale/locale';
import { eventBus } from '@/utils/event';

interface Props extends Partial<Pick<RootStore, 'rtc' | 'user' | 'meeting'>> {
  RTCInstance?: RootStore['rtc'];
  user?: RootStore['user'];
  meeting?: RootStore['meeting'];
}
const ApplicationsBtn: React.FC<Props> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer((props) => {
    const { RTCInstance, user, meeting } = props;
    const { formatMessage } = useIntl();

    const switchWhiteboard = React.useCallback(() => {
      if (RTCInstance?.breakLineReconnecting) {
        return;
      }
      if (!RTCInstance?.cid) {
        message.error({ content: '无效的会议' });
        return;
      }
      if (meeting?.screenUid) {
        message.info({
          content: formatMessage({ id: 'FAILED_TO_OPEN_WHITEBOARD_BECAUSE_OF_SCREEN_SHARING' }),
          noIcon: true,
        });
        return;
      }
      if (meeting?.codeEditorEnabled) {
        message.info({
          content: formatMessage({ id: 'FAILED_TO_OPEN_WHITEBOARD_BECAUSE_OF_CODE_EDITOR' }),
          noIcon: true,
        });
        return;
      }
      if (meeting?.whiteboardUid && meeting.whiteboardUid !== user?.mid) {
        message.info({
          content: formatMessage({ id: 'FAILED_TO_OPEN_WHITEBOARD_BECAUSE_OF_DUPLICATED' }),
          noIcon: true,
        });
        return;
      }
      if (
        meeting?.wbEnablePermission === WhiteboardEnablePermission.HOST &&
        meeting.hostUid &&
        meeting.hostUid !== user?.mid
      ) {
        message.info({
          content: formatMessage({ id: 'noPermission' }),
          noIcon: true,
        });
        return;
      }
      if (meeting?.whiteboardLoading) {
        return;
      }
      if (!meeting?.whiteboardUid) {
        meeting?.setWhiteboardLoading(true);
        elkLog('打开白板');
        API.enableWhiteboard(RTCInstance.cid)
          .then((res) => {
            elkLog('打开白板成功');
            meeting?.setWhiteboardWillClose(false);
            meeting?.setWhiteboardUid(
              user!.mid,
              {
                appkey: res.data?.appkey,
                nonce: res.data?.nonce,
                checksum: res.data?.checksum,
                curTime: res.data?.curTime,
              },
              res.data?.whiteboardChannelId
            );
          })
          .catch((e: any) => {
            if (e.status !== undefined) {
              let text: string;
              switch (e.status) {
                case 520001:
                  text = formatMessage({
                    id: 'FAILED_TO_OPEN_WHITEBOARD_BECAUSE_OF_SCREEN_SHARING',
                  });
                  break;
                case 520002:
                  text = formatMessage({
                    id: 'FAILED_TO_OPEN_WHITEBOARD_BECAUSE_OF_DUPLICATED',
                  });
                  break;
                case 511005:
                  text = formatMessage({
                    id: 'noPermission',
                  });
                  break;
                default:
                  text = e.message;
                  break;
              }
              message.info({ content: text, noIcon: true });
            } else {
              message.error({ content: e.message || String(e) });
            }
            meeting?.setWhiteboardLoading(false);
            elkReport({ type: '打开白板失败', logData: e });
          });
      } else {
        meeting.setWhiteboardWillClose(true);
      }
    }, [user, meeting, RTCInstance, formatMessage]);

    const switchCodeEditor = React.useCallback(() => {
      // if (focused?.focusedYuxid) {
      //   message.info({
      //     content: formatMessage({
      //       id: 'codeEditorFocused',
      //     }),
      //     noIcon: true,
      //   });
      //   return;
      // }
      if (RTCInstance?.breakLineReconnecting) {
        return;
      }
      if (!RTCInstance?.cid) {
        message.error({ content: '无效的会议' });
        return;
      }
      if (meeting?.screenUid) {
        message.info({
          content: formatMessage({
            id: 'FAILED_TO_OPEN_CODE_EDITOR_BECAUSE_OF_SCREEN_SHARING',
          }),
          noIcon: true,
        });
        return;
      }
      if (meeting?.whiteboardUid) {
        message.info({
          content: formatMessage({ id: 'FAILED_TO_OPEN_CODE_EDITOR_BECAUSE_OF_WHITEBOARD' }),
          noIcon: true,
        });
        return;
      }
      if (meeting?.codeEditorEnabled) {
        elkLog('关闭代码编辑器');
        meeting.closeEditor();
      } else {
        if (meeting?.codeEditorLoading) {
          return;
        }
        DATracker.track('openCodediter', {
          profile,
          meetingScheduleSource: new URLSearchParams().get('source') ?? '',
        });
        elkLog('打开代码编辑器');
        meeting?.setCodeEditorLoading(true);
        API.enableCodeEditor(RTCInstance.cid)
          .then((res) => {
            meeting?.setCodeEditorEnableUid(res?.data?.codeEditorEnableUid);
            meeting?.setCodeEditorEnabled(true);
            meeting?.setPeoplePanelShow(false);
            elkLog('打开代码编辑器成功');
          })
          .catch((e) => {
            if (e.status !== undefined) {
              let text: string;
              switch (e.status) {
                case 520001:
                  text = formatMessage({
                    id: 'FAILED_TO_OPEN_CODE_EDITOR_BECAUSE_OF_SCREEN_SHARING',
                  });
                  break;
                case 520002:
                  text = formatMessage({
                    id: 'FAILED_TO_OPEN_CODE_EDITOR_BECAUSE_OF_WHITEBOARD',
                  });
                  break;
                default:
                  text = e.message;
                  break;
              }
              message.info({ content: text, noIcon: true });
            } else {
              message.error({ content: e.message || String(e) });
            }
            meeting?.setCodeEditorLoading(false);
            elkReport({ type: '打开代码编辑器失败', logData: e });
          });
      }
    }, [meeting, RTCInstance, formatMessage]);

    useEffect(() => {
      eventBus.on('openCodeEditor', switchCodeEditor);
      return () => {
        eventBus.off('openCodeEditor', switchCodeEditor);
      };
    }, []);

    return <AppsBtn switchCodeEditor={switchCodeEditor} switchWhiteboard={switchWhiteboard} />;
  })
);

export default ApplicationsBtn;
